import React, { createContext, useContext, useEffect, useState } from "react";
import LovedOne from "../ui/components/lovedone/LovedOne";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import axios from "axios";
import { auth } from "../utils/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { useSignOut } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { consumeReturnUrl } from "../utils/pendingTributes";
import {
  emptyMemorialForm,
  startFreshMemorialWizard,
} from "../utils/memorialWizard";

const MemorialContext = createContext();

function safeParseSession(key, fallback = null) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    sessionStorage.removeItem(key);
    return fallback;
  }
}

export function MemorialContextProvider({ children }) {
  const userData = safeParseSession("user");
  const formDataStored = safeParseSession("formData");
  const navigate = useNavigate();
  const [user, setUser] = useState(userData);
  const [error, setError] = useState("");
  const [themes, setThemes] = useState([]);
  const [newThemes, setNewThemes] = useState(themes);
  const [selectedTheme, setSelectedTheme] = useState();
  const [options, setOptions] = React.useState([]);
  const [currentMemorial, setCurrentMemorial] = React.useState();
  const [formIsCompleted, setFormIsCompleted] = React.useState(
    formDataStored ? true : false
  );
  const [memorialID, setMemorialID] = React.useState();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
  const [showOption, setShowOption] = React.useState({
    title: "About your loved one",
    isCompleted: true,
    isActive: true,
    component: <LovedOne />,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isLegacyLogin, setIsLegacyLogin] = useState(true);
  const [signOut] = useSignOut(auth);
  const [name, setName] = useState("");
  const userFormData = safeParseSession("formData");

  const [formData, setFormData] = useState(
    formDataStored || emptyMemorialForm()
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const handleSelected = (itm) => {
    const currentIndex = options.findIndex((option) => option === itm);

    // Check if the current step is the first step or the previous step is completed
    if (currentIndex === 0 || options[currentIndex - 1].isCompleted) {
      // Create a new array with updated isActive values
      const updatedOptions = options.map((option) => ({
        ...option,
        isActive: option === itm,
      }));

      // Check if specific fields in formData are not empty
      const { firstName, lastName, relationship, gender } = formData;

      if (
        firstName !== "" &&
        lastName !== "" &&
        relationship !== "" &&
        gender !== ""
      ) {
        // Update the options array with updated isActive values
        setFormIsCompleted(true);
        setOptions(updatedOptions);

        // Set the showOption to the selected item
        setShowOption(itm);
      }
    } else {
      // Handle the case where the previous step is not completed
      console.log("You can't proceed until the previous step is completed.");
    }
  };

  function emailPasswordSignIn(email, password) {
    signInWithEmailAndPassword(auth, email, password)
      .then((currentuser) => {
        if (currentuser) {
          setIsLoading(false);
          setGoogleLoading(false);

          setLoginLoading(false);
          const data = {
            name: name,
            email: currentuser.user.email,
            firebaseUid: currentuser.user.uid,
          };
          fetch(`${backendUrl}/api/users`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          })
            .then((response) => response.json())
            .then((data) => {
              const userObj = buildUserState(currentuser.user, data.user);
              setUser(userObj);
              sessionStorage.setItem("user", JSON.stringify(userObj));
              setLoginLoading(false);
              navigateAfterAuth(data.user);
            })
            .catch((error) => {
              // Handle any errors that occur during the request
              console.error("Error creating user:", error);
              setError(error.message);
            });
        }
      })
      .catch((err) => {
        setError(err.message);
        setLoginLoading(false);
      });
  }

  function emailPasswordSignUp(email, password) {
    createUserWithEmailAndPassword(auth, email, password)
      .then((currentuser) => {
        if (currentuser) {
          setIsLoading(false);
          setGoogleLoading(false);
          setLoginLoading(false);
          const data = {
            name: name,
            email: currentuser.user.email,
          };
          fetch(`${backendUrl}/api/users`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          })
            .then((response) => response.json())
            .then((data) => {
              // Handle the successful creation of the user
              // setUser({ ...currentuser.user, meta: { ...data.user } });
              setIsLoading(false);
              navigate("/login");
            })
            .catch((error) => {
              // Handle any errors that occur during the request
              console.error("Error creating user:", error);
            });
        }
      })
      .catch((err) => {
        setError(err.message);
        console.log(err.message);
        setIsLoading(false);
      });
  }
  const delayedTrigger = (callback) => {
    setLoading(true);

    setTimeout(() => {
      callback();
      setLoading(false);
    }, 3000);
  };

  const buildUserState = (firebaseUser, dbUser) => ({
    email: firebaseUser.email,
    displayName: dbUser?.name || firebaseUser.displayName || "",
    uid: firebaseUser.uid,
    photoURL: firebaseUser.photoURL,
    meta: { ...dbUser },
  });

  const syncUserFromFirebase = async (firebaseUser, shouldNavigate = false) => {
    const data = {
      name: firebaseUser?.displayName || "",
      email: firebaseUser.email,
      firebaseUid: firebaseUser.uid,
    };
    const response = await fetch(`${backendUrl}/api/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`User sync failed (${response.status})`);
    }
    const result = await response.json();
    if (!result?.user) {
      throw new Error("User sync returned invalid data");
    }
    const userObj = buildUserState(firebaseUser, result.user);
    setUser(userObj);
    sessionStorage.setItem("user", JSON.stringify(userObj));
    if (shouldNavigate) {
      navigateAfterAuth(result.user);
    }
    return userObj;
  };

  const navigateAfterAuth = (userMeta) => {
    if (window.location.pathname === "/success") return;

    if (userFormData != null) {
      const title = userMeta?.hasPaid ? "Privacy options" : "Choose your plan";
      const updatedOptions = options.map((option) => ({
        ...option,
        isActive: option.title === title,
      }));
      const activeOption = updatedOptions.find((option) => option.title === title);
      setOptions(updatedOptions);
      if (activeOption) setShowOption(activeOption);
      window.location.href = "/creatememorial";
      return;
    }

    const returnUrl = consumeReturnUrl("/");
    navigate(returnUrl);
  };

  const navigateAfterGoogleSignIn = (userMeta) => {
    navigateAfterAuth(userMeta);
  };

  const completeGoogleSignIn = async (currentuser) => {
    const data = {
      name: currentuser?.displayName || "",
      email: currentuser.email,
      firebaseUid: currentuser.uid,
    };
    const response = await fetch(`${backendUrl}/api/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    const userObj = buildUserState(currentuser, result.user);
    setUser(userObj);
    sessionStorage.setItem("user", JSON.stringify(userObj));
    setIsLoading(false);
    setGoogleLoading(false);
    setLoginLoading(false);
    navigateAfterGoogleSignIn(result.user);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setIsLegacyLogin(false);
    try {
      let firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        firebaseUser = result.user;
      }
      await completeGoogleSignIn(firebaseUser);
    } catch (err) {
      setGoogleLoading(false);
      const ignoredCodes = [
        "auth/popup-closed-by-user",
        "auth/cancelled-popup-request",
      ];
      if (!ignoredCodes.includes(err?.code)) {
        setError(err.message || "Google sign-in failed");
      }
    }
  };

  const fetchUser = async () => {
    if (!user?.meta?._id) return;
    try {
      const response = await fetch(`${backendUrl}/api/users/${user.meta._id}`);
      if (response.ok) {
        const data = await response.json();
        const userObj = { ...user, meta: { ...data.user } };
        setUser(userObj);
        sessionStorage.setItem("user", JSON.stringify(userObj));
      } else {
        console.error("Failed to fetch user");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) return;
      const storedRaw = sessionStorage.getItem("user");
      let stored = null;
      try {
        stored = storedRaw ? JSON.parse(storedRaw) : null;
      } catch {
        stored = null;
      }
      if (!stored?.meta?._id || stored.email !== firebaseUser.email) {
        try {
          await syncUserFromFirebase(firebaseUser, false);
        } catch (e) {
          console.error("Auth sync failed:", e);
        }
      }
    });
    return unsub;
    // Mount-only: re-subscribing when syncUserFromFirebase identity changes would duplicate auth listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionMemoID = sessionStorage.getItem("memorialID");
  useEffect(() => {
    setMemorialID(sessionMemoID);
  }, [sessionMemoID]);

  const getMemorialDetails = async (userId, memorialId) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/users/${userId}/memorials/${memorialId}`
      );
      if (response.ok) {
        const data = await response.json();

        const { memorial } = data;

        sessionStorage.setItem(
          "formData",
          JSON.stringify({
            ...formData,
            firstName: memorial.firstName,
            lastName: memorial.lastName,
            gender: memorial.gender,
            relationship: memorial.relationship,
            country: memorial.country,
            birthDate: memorial.birthDate,
            birthPlace: memorial.birthPlace,
            passedDate: memorial.passedDate,
            deathPlace: memorial.deathPlace,
            deathCountry: memorial.deathCountry,
          })
        );

        // setFormData({
        //   ...formData,
        //   firstName: memorial.firstName,
        //   lastName: memorial.lastName,
        //   gender: memorial.gender,
        //   relationship: memorial.relationship,
        //   country: memorial.country,
        //   birthDate: memorial.birthDate,
        //   birthPlace: memorial.birthPlace,
        //   passedDate: memorial.passedDate,
        //   deathPlace: memorial.deathPlace,
        //   deathCountry: memorial.deathCountry,
        // });
      } else {
        console.error("Failed to fetch memorials:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching memorials:", error);
    }
  };

  useEffect(() => {
    const fetchThemes = async () => {
      const url = `${backendUrl || "http://localhost:5000"}/api/themes`;
      try {
        const response = await axios.get(url);
        const list = response.data?.themes ?? [];
        setThemes(Array.isArray(list) ? list : []);
        if (list.length === 0) console.warn("⚠️ GET /api/themes returned 0 themes. Backend DB?", url);
      } catch (error) {
        console.error("❌ Error fetching themes:", error?.message || error, "URL:", url);
      }
    };

    fetchThemes();
    // backendUrl comes from env and does not change at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Full wizard reset. keepNames: preserve first/last from My Memorials mini-form.
   * Always forces "About your loved one" again (dates/relationship cannot be edited later).
   */
  const resetMemorialWizard = (options = {}) => {
    const { keepNames = false } = options;
    const seed = keepNames
      ? { firstName: formData?.firstName || "", lastName: formData?.lastName || "" }
      : {};
    const nextForm = emptyMemorialForm(seed);

    setShowOption({
      title: "About your loved one",
      isCompleted: true,
      isActive: true,
      component: <LovedOne />,
    });
    setFormData(nextForm);
    setFormIsCompleted(false);
    setMemorialID("");
    setCurrentMemorial({});
    startFreshMemorialWizard();
    // Re-seed names into session only when intentionally kept (My Memorials Create).
    if (keepNames && (seed.firstName || seed.lastName)) {
      sessionStorage.setItem("formData", JSON.stringify(nextForm));
    }
  };

  const ResetForNewMemo = () => {
    resetMemorialWizard({ keepNames: false });
  };

  return (
    <MemorialContext.Provider
      value={{
        name,
        setName,
        isLegacyLogin,
        setIsLegacyLogin,
        formData,
        handleChange,
        user,
        emailPasswordSignIn,
        emailPasswordSignUp,
        error,
        setError,
        isLoading,
        themes,
        signOut,
        delayedTrigger,
        loading,
        setIsLoading,
        googleLoading,
        loginLoading,
        setGoogleLoading,
        setLoginLoading,
        handleGoogleSignIn,
        options,
        setOptions,
        showOption,
        setShowOption,
        handleSelected,
        setThemes,
        newThemes,
        setSelectedTheme,
        selectedTheme,
        setNewThemes,
        getMemorialDetails,
        setFormData,
        setCurrentMemorial,
        currentMemorial,
        fetchUser,
        setUser,
        ResetForNewMemo,
        resetMemorialWizard,
        formIsCompleted,
        setFormIsCompleted,
        memorialID,
        setMemorialID,
      }}
    >
      {children}
    </MemorialContext.Provider>
  );
}

export function useUserContext() {
  return useContext(MemorialContext);
}
